import { useMemo } from "react";
import { useTheme } from "@mui/material";
import { DefaultRawDatum, Pie, PieTooltipProps, type PieCustomLayerProps } from "@nivo/pie";
import { BasicTooltip } from "@nivo/tooltip";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { useUpdateChartData, DonutChartData } from "src/components/charts/donut-chart/useUpdateChartData";

export interface Total {
    label: string;
    value: number;
}

export interface Messages {
    loading?: string;
    noData?: string;
    total?: string;
}

export interface CircularChartProps {
    data?: DonutChartData[];
    isLoading?: boolean;
    onDataClick?: (id: string) => void;
    messages?: Messages;
    total?: number | string;
    size?: number;
    innerRadius?: number;
}

export const CircularChart = ({
    data = [],
    isLoading,
    messages = {},
    total,
    onDataClick,
    size = 212,
    innerRadius = 0.86,
}: CircularChartProps) => {
    const noData = noDataGiven(data);
    const pieEntries = useUpdateChartData(data, noData, isLoading);

    const handleClick = (data: DonutChartData) => {
        if (!onDataClick || isLoading || noData) return;
        onDataClick(data.id);
    };

    const tooltipComponent = useMemo(() => (noData || isLoading ? EmptyToolTip : ToolTip), [noData, isLoading]);

    const CenterText = useMemo(() => {
        if (isLoading) return Loading(messages.loading);
        if (noData) return NoData(messages.noData);
        return Total(total, messages.total);
    }, [isLoading, noData, total, messages]);

    return (
        <Pie
            data={pieEntries}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            colors={pieEntries.map((pieEntry) => pieEntry.color)}
            isInteractive={!isLoading && !noData}
            sortByValue={true}
            padAngle={1}
            cornerRadius={0}
            borderWidth={0}
            tooltip={tooltipComponent}
            enableArcLinkLabels={false}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            enableArcLabels={false}
            arcLabel="value"
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{
                from: "color",
                modifiers: [["opacity", 1.3]],
            }}
            innerRadius={innerRadius}
            activeInnerRadiusOffset={4}
            activeOuterRadiusOffset={4}
            layers={["arcs", "arcLabels", "arcLinkLabels", "legends", CenterText]}
            height={size}
            width={size}
            onClick={(datum) => handleClick(datum.data)}
            onMouseEnter={(_, event) => {
                if (onDataClick) event.currentTarget.style.cursor = "pointer";
            }}
            onMouseLeave={(_, event) => {
                if (onDataClick) event.currentTarget.style.cursor = "auto";
            }}
        />
    );
};

const ToolTip = (props: PieTooltipProps<DonutChartData>) => {
    return <BasicTooltip id={props.datum.label} value={props.datum.value} color={props.datum.color} enableChip />;
};

const EmptyToolTip = (props: PieTooltipProps<DonutChartData>) => {
    const theme: SenecFPPTheme = useTheme();

    return <BasicTooltip id={""} value={0} color={props.datum.color || theme.palette.neutralGray50.main} enableChip />;
};

const Total = (total?: number | string, message?: string) => (layerProps: PieCustomLayerProps<DefaultRawDatum>) => {
    const theme: SenecFPPTheme = useTheme();
    const { centerX, centerY } = layerProps;

    if (!total) return null;

    return (
        <text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="central" data-testid="total-text">
            <tspan x={centerX} dy="-1em" style={{ fontSize: "14px", fill: theme.palette.neutralGray80.main }}>
                {message || "Total"}
            </tspan>
            <tspan
                x={centerX}
                dy="1.5em"
                style={{ fontSize: "18px", fontWeight: "bold", fill: theme.palette.neutralGray100.main }}
            >
                {total}
            </tspan>
        </text>
    );
};

const Loading = (message?: string) => (layerProps: PieCustomLayerProps<DefaultRawDatum>) => {
    const theme: SenecFPPTheme = useTheme();
    const { centerX, centerY } = layerProps;

    return (
        <text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="central" data-testid="loading-text">
            <tspan x={centerX} style={{ fontSize: "14px", fill: theme.palette.neutralGray60.main }}>
                {message || "Loading…"}
            </tspan>
        </text>
    );
};

const NoData = (message?: string) => (layerProps: PieCustomLayerProps<DefaultRawDatum>) => {
    const theme: SenecFPPTheme = useTheme();
    const { centerX, centerY } = layerProps;

    return (
        <text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="central">
            <tspan x={centerX} style={{ fontSize: "14px", fill: theme.palette.neutralGray60.main }}>
                {message || "No data"}
            </tspan>
        </text>
    );
};

/** Returns true if the given data is an empty array or all values are 0. */
export const noDataGiven = (data: DonutChartData[]) => data.length === 0 || data.every((elem) => elem.value === 0);
